import React, { useEffect, useRef } from 'react'
import { loadConnectAndInitialize } from '@stripe/connect-js'
import { useNavigate } from 'react-router-dom'
import { Breadcrumbs } from '@enamelai/design-system'
import { getEnv } from '@utils/env'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import enamelaiLogo from '@assets/images/Enamelai_logo.png'
import { useStripeIntegration } from './useStripeIntegration'
import './StripeStyle.css'

const ACCOUNTING_SETTINGS_PATH = '/settings/insurance-and-payments/accounting'

const StripeOnboarding = (): React.ReactElement => {
  const { clinicId, startEmbeddedOnboarding } = useStripeIntegration()
  const { setHeaderTitle } = useSettingsHeader()
  const navigate = useNavigate()
  const embeddedContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let mounted = true
    let onboardingElement: ReturnType<
      Awaited<ReturnType<typeof loadConnectAndInitialize>>['create']
    > | null = null
    let didFailFirstFetch = false

    const mountEmbedded = async (): Promise<void> => {
      if (!clinicId || !embeddedContainerRef.current) {
        return
      }

      const publishableKey = getEnv('REACT_APP_STRIPE_PUBLISHABLE_KEY', '')
      if (!publishableKey) {
        navigate(ACCOUNTING_SETTINGS_PATH, { replace: true })
        return
      }

      try {
        embeddedContainerRef.current.innerHTML = ''
        const connectInstance = await loadConnectAndInitialize({
          publishableKey,
          fetchClientSecret: async () => {
            const fresh = await startEmbeddedOnboarding()
            if (!fresh) {
              throw new Error('Failed to refresh Stripe client secret')
            }
            return fresh
          },
        })
        if (!mounted || !embeddedContainerRef.current) return

        const onboarding = connectInstance.create('account-onboarding')
        onboarding.setOnExit(() =>
          navigate(ACCOUNTING_SETTINGS_PATH, { replace: true }),
        )
        onboardingElement = onboarding
        embeddedContainerRef.current.appendChild(onboarding)
      } catch {
        didFailFirstFetch = true
      } finally {
        if (didFailFirstFetch && mounted) {
          navigate(ACCOUNTING_SETTINGS_PATH, { replace: true })
        }
      }
    }

    void mountEmbedded()

    return () => {
      mounted = false
      if (onboardingElement && onboardingElement.parentNode) {
        onboardingElement.parentNode.removeChild(onboardingElement)
      }
    }
  }, [clinicId, navigate, startEmbeddedOnboarding])

  useEffect(() => {
    const backToAccounting = (): void => {
      void navigate(ACCOUNTING_SETTINGS_PATH)
    }
    setHeaderTitle(
      <Breadcrumbs
        showBackIcon
        onBackClick={backToAccounting}
        items={[
          { id: 'accounting', label: 'Accounting', onClick: backToAccounting },
          { id: 'current', label: 'Link to Stripe' },
        ]}
      />,
    )

    return () => setHeaderTitle(null)
  }, [navigate, setHeaderTitle])

  return (
    <div className="integrations-onboarding-shell">
      <div className="integrations-onboarding-brand">
        <div className="integrations-onboarding-brand-icon-wrap">
          <img src={enamelaiLogo} alt="EnamelAI logo" />
        </div>
        <span className="integrations-onboarding-brand-text">ENAMELAI</span>
      </div>
      <div className="integrations-onboarding-card">
        <div className="integrations-onboarding-copy">
          <h2>Business details</h2>
          <p>Let us know a few more details of your business</p>
        </div>
        <div
          ref={embeddedContainerRef}
          className="integrations-onboarding-embedded-container"
        />
      </div>
    </div>
  )
}

export default StripeOnboarding
